import { Loading } from "@components";
import dayjs from "dayjs";
import ComboFilters from "pages/Reports/ComboFilters";
import { ReportsAPI } from "providers/ReportsAPI";
import { useEffect, useMemo, useState } from "react";
import { If } from "shared/components/control-statements";
import { InputSearch } from "shared/components/InputSearch";
import { CPF } from "shared/tools/CPF";
import { Currency } from "shared/utils/Currency";
import { Numbers } from "shared/utils/Numbers";
import styles from "styles/pages/reports/reports.module.scss";

export default function OperationsRanking({ isCashback, filters }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const provider = useMemo(() => new ReportsAPI(), []);
  const fetchRankData = async () => {
    setLoading(true);
    const response = await provider.loyaltyOperations(
      "rank",
      {
        ...filters,
        operation: selectedOption.id === "concessao" ? "CREDIT" : "DEBIT",
        ...(filters.period === "FROM_THE_BEGINNING" && {
          start_date: null,
          end_date: null,
        }),
        ...(!filters.period && {
          start_date: dayjs().startOf("month").format("YYYY-MM-DD"),
          end_date: dayjs().endOf("month").format("YYYY-MM-DD"),
        }),
      },
      {
        page: currentPage,
      }
    );
    setData(response);
    setLoading(false);
  };

  const [selectedOption, setSelectedOption] = useState({
    name: "Concessão",
    id: "concessao",
  });
  const [search, setSearch] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  const filteredItems = search
    ? (data.data || []).filter(
        (i) =>
          (i.name || "").toLowerCase().includes(search) ||
          (i.doc || "").toLowerCase().includes(search) ||
          (i.phone || "").toLowerCase().includes(search)
      )
    : data.data;

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems =
    (Array.isArray(filteredItems) ? filteredItems : []) ||
    [].slice(indexOfFirstItem, indexOfLastItem);

  const totalPages = data.total_pages || 1;

  useEffect(() => {
    fetchRankData();
  }, [filters, selectedOption, currentPage]);

  if (!data) return null;

  const formatPhone = (phone) => {
    if (!phone) return null;
    const match = phone.match(/^\+55\s?(\d{2})\s?(\d{5}-\d{4})$/);
    if (!match) return phone;
    const [, ddd, number] = match;
    return `(${ddd}) ${number}`;
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  return (
    <div className={styles.box2}>
      <div className="d-flex align-center justify-between px-20">
        <div className="d-flex align-center gap-5">
          <p className={styles.label}>Ranking de operações</p>
        </div>
        <div className="d-flex align-center">
          <InputSearch
            design="two"
            placeholder="Buscar por..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className={styles.divider} />
          <ComboFilters
            options={[
              { name: "Concessão", id: "concessao" },
              { name: "Uso", id: "uso" },
            ]}
            onSelect={(option) => setSelectedOption(option)}
            selectedOption={
              selectedOption || { name: "Concessão", id: "concessao" }
            }
          />
        </div>
      </div>

      <div className={styles.hairline} />

      <div className={styles.tableFull} style={{ minHeight: 525 }}>
        <If cond={loading}>
          <div className="flex-1 d-flex align-center justify-center">
            <Loading />
          </div>
        </If>
        <If cond={!loading}>
          <table className="w-100">
            <thead>
              <tr>
                <th>TELEFONE DO CLIENTE</th>
                <th>DOCUMENTO DO CLIENTE</th>
                <th>NOME DO CLIENTE</th>
                <th>
                  {isCashback ? "VALOR TRANSACIONADO" : "PONTOS TRANSACIONADOS"}
                </th>
                <th>QTD. OPERAÇÕES</th>
              </tr>
            </thead>
            <tbody>
              {currentItems.map((item, index) => {
                return (
                  <tr key={index}>
                    <td style={{ width: "20%" }}>
                      <div className="d-flex align-center">
                        <p className={styles.value}>
                          {formatPhone(item.phone)}
                        </p>
                      </div>
                    </td>
                    <td style={{ width: "20%" }}>
                      <div className="d-flex align-center">
                        <p className={styles.value}>
                          {!item.doc ? "---" : CPF.mask(item.doc)}
                        </p>
                      </div>
                    </td>
                    <td style={{ width: "20%" }}>
                      <div className="d-flex align-center">
                        <p className={styles.value}>
                          {!item.name ? "---" : item.name}
                        </p>
                      </div>
                    </td>
                    <td style={{ width: "20%" }}>
                      <div className="d-flex align-center">
                        <p className={styles.value}>
                          {isCashback
                            ? Currency.formatCurrency(item.total)
                            : `${Numbers.formatInt(item.total)} pts`}
                        </p>
                      </div>
                    </td>
                    <td style={{ width: "20%" }}>
                      <div className="d-flex align-center">
                        <p className={styles.value}>{item.operations_count}</p>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </If>

        <div className={styles.pagination}>
          <button
            disabled={currentPage === 1 || !totalPages}
            onClick={() => handlePageChange(currentPage - 1)}
          >
            <i className="fal fa-arrow-left" />
          </button>

          <span className={styles.page}>
            Página <strong>{currentPage || 1}</strong> de{" "}
            <strong>{totalPages || 1}</strong>
          </span>

          <button
            disabled={currentPage === totalPages}
            onClick={() => handlePageChange(currentPage + 1)}
          >
            <i className="fal fa-arrow-right" />
          </button>
        </div>
      </div>
    </div>
  );
}
